import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, Trash2, Upload } from "lucide-react";
import { API, photoUrl } from "@/lib/site";

const opts = { withCredentials: true };
const errMsg = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo guardar");

const PhotoCard = ({ p, i, total, onSave, onMove, onDelete }) => {
  const [caption, setCaption] = useState(p.caption ?? "");
  const dirty = caption !== (p.caption ?? "");
  return (
    <article data-testid={`admin-photo-${p.id}`} className="rounded-2xl bg-white/70 border border-jungle/10 overflow-hidden flex flex-col">
      <img src={photoUrl(p.url)} alt={caption} className="aspect-[4/3] w-full object-cover" />
      <div className="p-4 flex flex-col gap-3">
        <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-jungle/70">Texto de la foto</label>
        <input value={caption} maxLength={160} onChange={(e) => setCaption(e.target.value)} placeholder="Ej.: Viernes de antídoto" data-testid={`admin-photo-caption-${p.id}`} className="rounded-xl border border-jungle/20 bg-paper px-3 py-2 text-sm" />
        <div className="flex items-center gap-2">
          <button disabled={!dirty} onClick={() => onSave(p.id, caption)} data-testid={`admin-photo-save-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-mango px-3 py-1.5 text-xs font-bold uppercase text-paper disabled:opacity-40"><Check size={13} /> Guardar</button>
          <button disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Subir" data-testid={`admin-photo-up-${p.id}`} className="rounded-full border border-jungle/20 p-1.5 disabled:opacity-30"><ArrowUp size={14} /></button>
          <button disabled={i === total - 1} onClick={() => onMove(i, 1)} aria-label="Bajar" data-testid={`admin-photo-down-${p.id}`} className="rounded-full border border-jungle/20 p-1.5 disabled:opacity-30"><ArrowDown size={14} /></button>
          <button onClick={() => onDelete(p.id)} data-testid={`admin-photo-delete-${p.id}`} className="ml-auto inline-flex items-center gap-1 text-xs text-red-700 hover:underline"><Trash2 size={13} /> Quitar</button>
        </div>
      </div>
    </article>
  );
};

export const AdminGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/gallery`);
    setPhotos(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { load().catch(() => toast.error("No se pudo cargar la galería")); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Elige una imagen");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/gallery`, fd, opts);
      setPhotos((p) => [...p, data]);
      setCaption("");
      fileRef.current.value = "";
      toast.success("Foto añadida a la galería");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const onSave = async (id, text) => {
    try {
      const { data } = await axios.patch(`${API}/admin/gallery/${id}`, { caption: text }, opts);
      setPhotos((p) => p.map((x) => (x.id === id ? data : x)));
      toast.success("Texto guardado");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const onMove = async (i, dir) => {
    const a = photos[i];
    const b = photos[i + dir];
    const next = [...photos];
    next[i] = { ...b, order: a.order };
    next[i + dir] = { ...a, order: b.order };
    setPhotos(next);
    await Promise.all([
      axios.patch(`${API}/admin/gallery/${a.id}`, { order: b.order }, opts),
      axios.patch(`${API}/admin/gallery/${b.id}`, { order: a.order }, opts),
    ]).catch(() => { toast.error("No se pudo reordenar"); load(); });
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Quitar esta foto de la galería?")) return;
    await axios.delete(`${API}/admin/gallery/${id}`, opts);
    setPhotos((p) => p.filter((x) => x.id !== id));
  };

  return (
    <section data-testid="admin-gallery">
      <form onSubmit={upload} className="rounded-2xl bg-white/70 border border-jungle/10 p-5 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="admin-gallery-upload-form">
        <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-[0.14em] text-jungle/70">
          Imagen (JPG, PNG o WEBP, máx. 10 MB)
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" data-testid="admin-gallery-file" className="text-sm font-normal normal-case tracking-normal" />
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-[0.14em] text-jungle/70">
          Texto de la foto (opcional)
          <input value={caption} maxLength={160} onChange={(e) => setCaption(e.target.value)} placeholder="Ej.: El cumple de Laura" data-testid="admin-gallery-caption" className="rounded-xl border border-jungle/20 bg-paper px-3 py-2 text-sm font-normal normal-case tracking-normal" />
        </label>
        <button disabled={busy} data-testid="admin-gallery-upload" className="btn-shine inline-flex items-center justify-center gap-2 rounded-full bg-mango px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-paper">
          <Upload size={14} className="relative z-[2]" /><span className="relative z-[2]">{busy ? "Subiendo..." : "Subir foto"}</span>
        </button>
      </form>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {photos.map((p, i) => (
          <PhotoCard key={p.id} p={p} i={i} total={photos.length} onSave={onSave} onMove={onMove} onDelete={onDelete} />
        ))}
      </div>
    </section>
  );
};
